import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import EmailNotReceivedModal from "../../components/email-not-received-modal";
import { useAuth } from "../../context/authContext";
import { requestEmailCode, verifyEmailCode } from "../../api/auth";
import { getAuthApiError } from "../../utils/auth-api-error";
import { HelpLink } from "../../common/help";
import LoginShell from "./LoginShell";
import styles from "./style.module.css";

const RESEND_COOLDOWN_SECONDS = 60;

type LoginCodeLocationState = {
  email?: string;
};

function formatCountdown(seconds: number): string {
  const mm = String(Math.floor(seconds / 60)).padStart(2, "0");
  const ss = String(seconds % 60).padStart(2, "0");
  return `${mm}:${ss}`;
}

function LoginCodePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { status, setUser } = useAuth();
  const codeInputRef = useRef<HTMLInputElement>(null);

  const state = (location.state as LoginCodeLocationState | null) ?? {};
  const email = state.email ?? "";

  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [resendIn, setResendIn] = useState(RESEND_COOLDOWN_SECONDS);
  const [helpOpen, setHelpOpen] = useState(false);

  useEffect(() => {
    if (!email) {
      navigate("/login", { replace: true });
    }
  }, [email, navigate]);

  useEffect(() => {
    if (status === "authed") {
      navigate("/", { replace: true });
    }
  }, [status, navigate]);

  useEffect(() => {
    if (resendIn <= 0) return;
    const t = window.setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => window.clearTimeout(t);
  }, [resendIn]);

  useEffect(() => {
    codeInputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (code.length !== 6) return;

    let cancelled = false;
    (async () => {
      setError(null);
      setSubmitting(true);
      try {
        const me = await verifyEmailCode(email, code);
        if (cancelled) return;
        setUser(me);
        navigate("/", { replace: true });
      } catch (e) {
        if (!cancelled) {
          setError(getAuthApiError(e, "Не удалось войти. Попробуйте ещё раз."));
        }
      } finally {
        if (!cancelled) setSubmitting(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [code, email, navigate, setUser]);

  const resend = useCallback(async () => {
    if (resendIn > 0 || submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      await requestEmailCode(email);
      setResendIn(RESEND_COOLDOWN_SECONDS);
      setCode("");
    } catch (e) {
      setError(getAuthApiError(e, "Не удалось переотправить код."));
    } finally {
      setSubmitting(false);
    }
  }, [email, resendIn, submitting]);

  const openSupport = useCallback(() => {
    setHelpOpen(false);
    window.open(HelpLink, "_blank", "noopener,noreferrer");
  }, []);

  if (status === "loading" || !email) return null;

  return (
    <LoginShell onBack={() => navigate("/login")}>
      <h1 className={styles.title}>Введите код</h1>
      <p className={styles.subtitle}>Отправили его на {email}</p>

      <form className={styles.form} onSubmit={(e) => e.preventDefault()}>
        <input
          ref={codeInputRef}
          className={styles.codeInput}
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          placeholder="••••••"
          maxLength={6}
          value={code}
          onChange={(e) => {
            setError(null);
            setCode(e.target.value.replace(/\D/g, "").slice(0, 6));
          }}
          disabled={submitting}
        />
        {error && <p className={styles.error}>{error}</p>}
      </form>

      <div className={styles.footer}>
        {resendIn > 0 ? (
          <p className={styles.resend}>
            Отправить повторно через {formatCountdown(resendIn)}
          </p>
        ) : (
          <button
            type="button"
            className={styles.textButton}
            onClick={() => void resend()}
            disabled={submitting}
          >
            Отправить повторно
          </button>
        )}
        <button
          type="button"
          className={styles.textButton}
          onClick={() => setHelpOpen(true)}
        >
          Не пришло письмо?
        </button>
      </div>

      <EmailNotReceivedModal
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        onSupport={openSupport}
      />
    </LoginShell>
  );
}

export default LoginCodePage;
